// @ts-check
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@codex-brightworld/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CSSTransition } from 'react-transition-group';
import NavigationItem from './NavigationItem';

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% - 55px);
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }

  &.l1-transition-enter {
    transform: translateX(-100%);
  }

  &.l1-transition-enter-active {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  &.l1-transition-exit {
    transform: translateX(0);
  }

  &.l1-transition-exit-active {
    transform: translateX(-100%);
    transition: transform 200ms ease-out;
  }

  &.l2-transition-enter {
    transform: translateX(100%);
  }

  &.l2-transition-enter-active {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  &.l2-transition-exit {
    transform: translateX(0);
  }

  &.l2-transition-exit-active {
    transform: translateX(-100%);
    transition: transform 200ms ease-out;
  }

  &.l2-transition-reverse-enter {
    transform: translateX(-100%);
  }

  &.l2-transition-reverse-enter-active {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  &.l2-transition-reverse-exit {
    transform: translateX(0);
  }

  &.l2-transition-reverse-exit-active {
    transform: translateX(100%);
    transition: transform 200ms ease-out;
  }

  &.l3-transition-enter {
    transform: translateX(100%);
  }

  &.l3-transition-enter-active {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  &.l3-transition-exit {
    transform: translateX(0);
  }

  &.l3-transition-exit-active {
    transform: translateX(100%);
    transition: transform 200ms ease-out;
  }
`;

const NavigationTitleWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 24px 0;
  display: block;
  margin: 0;
`;

const NavigationTitle = styled.h2`
  font-size: 14px;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily}, Helvetica, Arial, Sans-Serif;
  font-weight: bold;
  margin: 0;
`;

const CatalogNavigation = styled.ul`
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 16px;
  display: block;
  margin: 0;
  list-style: none;
`;

const PagesNavigation = styled.ul`
  width: 100%;
  box-sizing: border-box;
  display: block;
  margin: 0;
  list-style: none;
  padding: 0;
`;

const Link = styled.a`
  display: flex;
  gap: 15px;
  color: ${({ theme }) => theme.colors.neutral.currysBlack};
  text-decoration: none;
  margin-bottom: 12px;
  padding: 6px 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const TopBarNavigation = styled.ul`
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  display: block;
  margin: 0;
  list-style: none;
  background-color: ${({ theme }) => theme.colors.neutral.grey[0]};
`;

const TopBarLink = styled.a`
  display: block;
  color: ${({ theme }) => theme.colors.neutral.currysBlack};
  text-decoration: none;
  padding: 6px 0;
  font-size: 14px;
  line-height: 24px;
  margin-bottom: 12px;

  &:hover {
    text-decoration: underline;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const TopBarLinkItem = styled.li`
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const BackButton = styled.button`
  margin: 18px 16px 0;
  line-height: 24px;
  padding: 6px 6px 6px 0;
  font-size: 14px;
  display: flex;
  gap: 15px;
  color: ${({ theme }) => theme.colors.neutral.currysBlack};
  cursor: pointer;
  border: none;
  background-color: transparent;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily}, Helvetica, Arial, Sans-Serif;

  &:hover {
    text-decoration: underline;
  }
`;

const NavigationItems = (props) => {
  const [subcategory, setSubcategory] = useState(null);

  const subcategoryBackText =
    props.level < 2 ? 'Back to main menu' : `Back to ${props.title}`;

  const goBack = () => {
    props.setActiveLevel(props.level - 1);
    props.setDirection('backward');
  };

  const selectSubcategory = (category) => {
    setSubcategory(category);
    props.setDirection('forward');
    props.setActiveLevel(props.level + 1);
  };
  return (
    <>
      <CSSTransition
        in={props.level === props.activeLevel}
        timeout={200}
        classNames={
          props.level === 2 && props.direction === 'backward'
            ? 'l2-transition-reverse'
            : `l${props.level}-transition`
        }
        unmountOnExit
      >
        <Wrapper>
          {props.level !== 1 && (
            <BackButton onClick={goBack}>
              <Icon icon="arrowright" rotate={180} />
              {props.goBackText}
            </BackButton>
          )}
          <NavigationTitleWrapper>
            <Link href={props.link}>
              <NavigationTitle>{props.title}</NavigationTitle>
            </Link>
          </NavigationTitleWrapper>
          <CatalogNavigation>
            {props.nav?.map((d, i) => (
              <NavigationItem
                key={d.label}
                label={d.label}
                link={d.link}
                icon={d.icon}
                onNavigationClick={
                  d.nav?.length ? () => selectSubcategory(d) : null
                }
              />
            ))}
          </CatalogNavigation>
          {props.pages && (
            <PagesNavigation>
              {props.pages.map((d, i) => (
                <NavigationItem
                  key={`${i}: ${d.label}`}
                  label={d.label}
                  link={d.link}
                  hasBorder
                  onNavigationClick={
                    d.nav?.length ? () => selectSubcategory(d) : null
                  }
                />
              ))}
            </PagesNavigation>
          )}
          {props.topBar && (
            <TopBarNavigation>
              {props.topBar.map((d, i) => (
                <TopBarLinkItem key={`${i}: ${d.label}`}>
                  <TopBarLink href={d.link}>{d.label}</TopBarLink>
                </TopBarLinkItem>
              ))}
            </TopBarNavigation>
          )}
        </Wrapper>
      </CSSTransition>
      {props.level < 3 && (
        <NavigationItems
          title={subcategory?.label}
          link={subcategory?.link}
          goBackText={subcategoryBackText}
          nav={subcategory ? [].concat(...subcategory.nav) : null}
          level={props.level + 1}
          activeLevel={props.activeLevel}
          setActiveLevel={props.setActiveLevel}
          direction={props.direction}
          setDirection={props.setDirection}
        />
      )}
    </>
  );
};

NavigationItems.propTypes = {
  title: PropTypes.string,
  link: PropTypes.string,
  goBackText: PropTypes.string,
  nav: PropTypes.arrayOf(PropTypes.object),
  pages: PropTypes.arrayOf(PropTypes.object),
  topBar: PropTypes.arrayOf(PropTypes.object),
  level: PropTypes.number,
  activeLevel: PropTypes.number,
  setActiveLevel: PropTypes.func,
  direction: PropTypes.string,
  setDirection: PropTypes.func
};

NavigationItems.defaultProps = {
  title: 'Shop by category',
  link: '',
  goBackText: '',
  nav: undefined,
  pages: undefined,
  topBar: undefined,
  level: 0,
  activeLevel: 1,
  setActiveLevel: null,
  direction: '',
  setDirection: null
};

export default NavigationItems;
